/**
 * PC Skin - Color schemes for Base components
 *
 * The PC skin uses a Windows-style desktop UI theme. Each color scheme defines
 * the player-specific ellipse (pawn spot) gradient colors and shadow/dark fill
 * used to distinguish each player's home base.
 *
 * Visual structure:
 * - Monitor outer frame with rounded corners (#ddd)
 * - Screen area with sky-blue gradient background
 * - Title bar in blue (#2468ff) with "Ludoka.exe" text
 * - Window border in blue (#2468ff)
 * - 4 ellipse pawn spots with player-specific gradient fill
 * - Green taskbar at bottom with Start button and system clock
 */

export interface PcBaseColorScheme {
  name: string;
  ariaLabel: string;
  /** Shadow/dark fill color behind each pawn ellipse */
  ellipseShadow: string;
  /** Gradient start color (leftmost stop) for pawn ellipses */
  gradientStart: string;
  /** Gradient stop 1 at 27% */
  gradientStop1: string;
  /** Gradient stop 2 at 49% */
  gradientStop2: string;
  /** Gradient stop 3 at 68% */
  gradientStop3: string;
  /** Gradient stop 4 at 86% */
  gradientStop4: string;
  /** Gradient end color (rightmost stop) for pawn ellipses */
  gradientEnd: string;
}

export const PC_BASE_COLOR_SCHEMES = {
  blue: {
    name: 'blue',
    ariaLabel: 'Blue player home base',
    ellipseShadow: '#00074c',
    gradientStart: '#555eff',
    gradientStop1: '#525cfb',
    gradientStop2: '#4c57f2',
    gradientStop3: '#414fe1',
    gradientStop4: '#3243ca',
    gradientEnd: '#2237b2'
  },
  red: {
    name: 'red',
    ariaLabel: 'Red player home base',
    ellipseShadow: '#4c0000',
    gradientStart: '#ff556a',
    gradientStop1: '#fb5267',
    gradientStop2: '#f24c5d',
    gradientStop3: '#e1414e',
    gradientStop4: '#ca3238',
    gradientEnd: '#b22222'
  },
  green: {
    name: 'green',
    ariaLabel: 'Green player home base',
    ellipseShadow: '#044c00',
    gradientStart: '#52ffbd',
    gradientStop1: '#4ffbb9',
    gradientStop2: '#49f2b0',
    gradientStop3: '#3ee19f',
    gradientStop4: '#2fca88',
    gradientEnd: '#22b573'
  },
  yellow: {
    name: 'yellow',
    ariaLabel: 'Yellow player home base',
    ellipseShadow: '#4f3700',
    gradientStart: '#fffa55',
    gradientStop1: '#fbf752',
    gradientStop2: '#f2ed4c',
    gradientStop3: '#e1de41',
    gradientStop4: '#cac832',
    gradientEnd: '#b2b222'
  }
} as const satisfies Record<string, PcBaseColorScheme>;
